import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from './LanguageSwitcher'
import { User, ArrowUp } from 'lucide-react'
import LandingPage from './LandingPage'
import Dashboard from './Dashboard'
import CreateExercise from './CreateExercise'
import ExerciseList from './ExerciseList'
import EditExercise from './EditExercise'
import Contact from './Contact'
import AdminPanel from './AdminPanel'
import WorkoutReflections from './WorkoutReflections'
import PastBadgesPage from './PastBadgesPage'
import ErrorBoundary from './ErrorBoundary'
import PullToRefresh from './PullToRefresh'
import PWAInstallModal from './PWAInstallModal'

const App = ({ currentUser }) => {
  const { t, i18n } = useTranslation()
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)

  useEffect(() => {
    if (!currentUser) return
    const utmRaw = localStorage.getItem('utm_params')
    if (!utmRaw) return
    localStorage.removeItem('utm_params')
    try {
      const utmData = JSON.parse(utmRaw)
      fetch('/api/v1/users/save_utm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify(utmData)
      }).catch(() => {})
    } catch (_) {}
  }, [currentUser])
  const [currentPage, setCurrentPage] = useState('dashboard') // 'dashboard' | 'create_exercise' | 'exercise_list' | 'edit_exercise' | 'contact'
  const [editingExercise, setEditingExercise] = useState(null)
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <>
      <PullToRefresh>
        <div className="min-h-screen bg-[#111] text-white flex flex-col">
          {!currentUser && (
            <header className="bg-[#111] pt-6 px-6 pb-2 flex flex-col items-center justify-center text-center relative">



              {/* Desktop Language Switcher */}
              <div className="absolute top-6 left-6 hidden md:block z-10">
                <LanguageSwitcher />
              </div>

              {/* Desktop Buttons */}
              <div className="absolute top-6 right-6 hidden md:flex gap-3 items-center z-10">
                <a href={`/users/sign_in?locale=${i18n.language}`} className="bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-2 px-4 rounded-lg hover:opacity-90 transition-opacity text-sm">
                  {t('login')}
                </a>
                <a href={`/users/sign_up?locale=${i18n.language}`} className="bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-2 px-4 rounded-lg hover:opacity-90 transition-opacity text-sm">
                  {t('signup')}
                </a>
              </div>

              {/* Mobile User Icon */}

              <div className="relative w-full flex justify-center items-center mb-2">
                <button
                  onClick={() => setIsBottomSheetOpen(true)}
                  className="absolute right-[-14px] md:hidden text-gray-400 hover:text-white transition-colors flex flex-col items-center gap-0.5 top-1/2 -translate-y-1/2"
                >
                  <div className="relative">
                    <User color="#CCCCCC" />
                    <div className="absolute top-0 right-0 w-2.5 h-2.5 bg-orange-500 rounded-full border-2 border-[#111]"></div>
                  </div>
                  <span className="text-[9px] font-bold">{t('menu')}</span>
                </button>

                <div className="flex items-center gap-3">
                  {/* Dumbbell Icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    className="w-8 h-8 text-orange-500"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6.5 6.5l11 11" />
                    <path d="M21 21l-1-1" />
                    <path d="M3 3l1 1" />
                    <path d="M18 22l4-4" />
                    <path d="M2 6l4-4" />
                    <path d="M3 10l7-7" />
                    <path d="M14 21l7-7" />
                  </svg>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
                    Anytime Tracker
                  </h1>
                </div>
              </div>
              <h1 className="text-gray-400 text-sm">{t('app_description')}</h1>


            </header>
          )}
          {/* If logged in, Dashboard handles its own header/layout */}
          <main className="flex-grow">
            {currentUser ? (
              <ErrorBoundary>
                {(() => {
                  switch (currentPage) {
                    case 'create_exercise':
                      return <CreateExercise onBack={() => setCurrentPage('dashboard')} currentUser={currentUser} />
                    case 'exercise_list':
                      return <ExerciseList
                        currentUser={currentUser}
                        onBack={() => setCurrentPage('dashboard')}
                        onEdit={(exercise) => {
                          setEditingExercise(exercise)
                          setCurrentPage('edit_exercise')
                        }}
                      />
                    case 'edit_exercise':
                      return <EditExercise
                        exercise={editingExercise}
                        currentUser={currentUser}
                        onBack={() => setCurrentPage('exercise_list')}
                        onSave={() => setCurrentPage('exercise_list')}
                      />
                    case 'contact':
                      return <Contact
                        currentUser={currentUser}
                        onBack={() => setCurrentPage('dashboard')}
                      />
                    case 'admin_panel':
                      return <AdminPanel
                        onBack={() => setCurrentPage('dashboard')}
                      />
                    case 'reflections':
                      return <WorkoutReflections
                        onBack={() => setCurrentPage('dashboard')}
                        currentUser={currentUser}
                      />
                    case 'past_badges':
                      return <PastBadgesPage
                        onBack={() => setCurrentPage('dashboard')}
                        currentUser={currentUser}
                      />
                    default:
                      return <Dashboard
                        onOpenMenu={() => setIsBottomSheetOpen(true)}
                        onNavigate={(page) => setCurrentPage(page)}
                        currentUser={currentUser}
                      />
                  }
                })()}
              </ErrorBoundary>
            ) : <LandingPage />}
          </main>

        </div>
      </PullToRefresh>
      
      {(currentPage === 'reflections' || ((!currentUser || currentPage === 'dashboard') && showScrollTop)) && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-4 p-4 bg-[#2c2c2e]/20 text-white/50 rounded-full shadow-2xl border border-white/10 hover:bg-orange-500 hover:text-white transition-all z-50 animate-in fade-in zoom-in duration-300"
          aria-label="Scroll to top"
        >
          <ArrowUp size={24} />
        </button>
      )}

      {/* Mobile Bottom Sheet */}
      {
        isBottomSheetOpen && (
          <div className="fixed inset-0 z-50 flex items-end justify-center">
            {/* Overlay */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsBottomSheetOpen(false)}
            ></div>

            {/* Sheet */}
            <div className="relative w-full bg-[#1c1c1e] rounded-t-xl p-6 pb-12 shadow-2xl transform transition-transform animate-in slide-in-from-bottom duration-300 md:max-w-md md:rounded-xl md:mb-6 md:pb-6">
              <div className="w-12 h-1 bg-gray-600 rounded-full mx-auto mb-6 md:hidden"></div>

              <div className="flex flex-col gap-2.5">
                {!currentUser ? (
                  <>
                    <a href={`/users/sign_in?locale=${i18n.language}`} className="w-full bg-[#2c2c2e] text-white font-bold py-3 rounded-lg hover:bg-[#3a3a3c] transition-colors text-center border border-gray-700">
                      {t('login')}
                    </a>
                    <a href={`/users/sign_up?locale=${i18n.language}`} className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-3 rounded-lg hover:opacity-90 transition-opacity text-center">
                      {t('signup')}
                    </a>
                    <div className="w-full flex justify-center pt-2">
                      <LanguageSwitcher />
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setCurrentPage('exercise_list')
                        setIsBottomSheetOpen(false)
                      }}
                      className="w-full bg-[#2c2c2e] text-white font-bold py-3 rounded-lg hover:bg-[#3a3a3c] transition-colors text-center border border-gray-700"
                    >
                      {t('workout_list')}
                    </button>
                    <button
                      onClick={() => {
                        setCurrentPage('create_exercise')
                        setIsBottomSheetOpen(false)
                      }}
                      className="w-full bg-[#2c2c2e] text-white font-bold py-3 rounded-lg hover:bg-[#3a3a3c] transition-colors text-center border border-gray-700"
                    >
                      {currentUser?.admin ? t('add_workout') : t('add_personal_workout')}
                    </button>
                    <a href={`/users/edit?locale=${i18n.language}`} className="w-full bg-[#2c2c2e] text-white font-bold py-3 rounded-lg hover:bg-[#3a3a3c] transition-colors text-center border border-gray-700"
                    >
                      {t('edit_profile')}
                    </a>
                    <a href={`/settings?locale=${i18n.language}`} className="w-full bg-[#2c2c2e] text-white font-bold py-3 rounded-lg hover:bg-[#3a3a3c] transition-colors text-center border border-gray-700">
                      {t('settings.title')}
                    </a>
                    <a href={`/proteins?locale=${i18n.language}`} className="w-full bg-[#2c2c2e] text-white font-bold py-3 rounded-lg hover:bg-[#3a3a3c] transition-colors text-center border border-gray-700">
                      {t('feature_protein_search')}
                    </a>
                    <button
                      onClick={() => {
                        setCurrentPage('contact')
                        setIsBottomSheetOpen(false)
                      }}
                      className="w-full bg-[#2c2c2e] text-white font-bold py-3 rounded-lg hover:bg-[#3a3a3c] transition-colors text-center border border-gray-700"
                    >
                      {t('contact_menu_link')}
                    </button>
                    {currentUser.admin && (
                      <button
                        onClick={() => {
                          setCurrentPage('admin_panel')
                          setIsBottomSheetOpen(false)
                        }}
                        className="w-full bg-[#2c2c2e] text-orange-500 font-bold py-3 rounded-lg hover:bg-[#3a3a3c] transition-colors text-center border border-orange-500/20"
                      >
                        管理画面
                      </button>
                    )}
                    <a href={`/users/sign_out?locale=${i18n.language}`} data-turbo-method="delete" className="w-full bg-red-500/10 text-red-500 font-bold py-3 rounded-lg hover:bg-red-500/20 transition-colors text-center border border-red-500/20">
                      {t('logout')}
                    </a>
                    <div className="w-full flex justify-center pt-2">
                      <LanguageSwitcher />
                    </div>
                  </>
                )}
              </div>

              <button
                onClick={() => setIsBottomSheetOpen(false)}
                className="w-full mt-4 py-2 text-gray-500 text-sm hover:text-gray-300 transition-colors"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        )
      }
      {currentUser && <PWAInstallModal pwaInstalled={currentUser.pwa_installed} />}
    </>
  )
}

export default App
